import { lazy, type ComponentType } from "react";
import type { TViewerProps } from "@modules/viewers/types";

// ── Lazy imports for all viewers ────────────────────────────────
const ImageViewer = lazy(() =>
  import("../image/components/ImageViewer").then((m) => ({
    default: m.ImageViewer,
  })),
);

const VideoViewer = lazy(() =>
  import("../video/components/VideoViewer").then((m) => ({
    default: m.VideoViewer,
  })),
);

const TextViewer = lazy(() =>
  import("../text/components/TextViewer").then((m) => ({
    default: m.TextViewer,
  })),
);

const PdfViewer = lazy(() =>
  import("../pdf/components/PdfViewer").then((m) => ({
    default: m.PdfViewer,
  })),
);

const IFCViewer = lazy(() =>
  import("../ifc/components/IfcViewer").then((m) => ({
    default: m.default, // default export
  })),
);

const CadViewer = lazy(() =>
  import("../cad/components/CadViewer").then((m) => ({
    default: m.CadViewer,
  })),
);

// ── Viewer format sets ────────────────────────────────
export const IMAGE_FORMATS = new Set([
  "image",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
]);
const VIDEO_FORMATS = new Set(["video", "mp4", "webm", "mov"]);
const TEXT_FORMATS = new Set(["txt", "md", "json", "csv", "log"]);
const PDF_FORMATS = new Set(["pdf", "application/pdf"]);
const IFC_FORMATS = new Set(["ifc"]);
const CAD_FORMATS = new Set(["dwg", "dxf"]);

// ── Wrap IFCViewer to match TViewerProps ────────────────────────────────
const IfcViewerWrapper: ComponentType<TViewerProps> = (props) => {
  // Map the generic `url` prop to `link` expected by IFCViewer
  return <IFCViewer link={props.url} />;
};

// ── Resolver ────────────────────────────────
export function resolveViewer(
  format: string,
): ComponentType<TViewerProps> | null {
  const f = format.toLowerCase();

  if (IMAGE_FORMATS.has(f)) return ImageViewer;
  if (VIDEO_FORMATS.has(f)) return VideoViewer;
  if (TEXT_FORMATS.has(f)) return TextViewer;
  if (PDF_FORMATS.has(f)) return PdfViewer;
  if (IFC_FORMATS.has(f)) return IfcViewerWrapper;
  if (CAD_FORMATS.has(f)) return CadViewer;

  return null;
}
